import { ApplicationContext, ApplicationUpdate } from '@/context/ApplicationContext';
import useDebouncedLocalStorage from '@/hooks/useDebouncedLocalStorage';
import { Application } from '@/types/application';
import { BackendApplication } from '@/types/backend';
import { emptyApplication } from '@/util/application';
import { useState } from 'react';

// Stored as one value so a restore can never pair one application's content with another's backend record.
type StoredSession = {
  application: Application;
  backendApplication?: BackendApplication;
};

export const ApplicationProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useDebouncedLocalStorage<StoredSession>('currentSession', {
    application: emptyApplication
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const setApplication = (app: ApplicationUpdate, isNewApplication?: boolean) => {
    setHasUnsavedChanges(!isNewApplication);
    setSession((previous) => {
      const newApp = (app instanceof Function ? app(previous.application) : app) ?? emptyApplication;
      return { ...previous, application: isNewApplication ? newApp : { ...newApp, generated: '' } };
    });
  };

  const setBackendApplication = (backendApplication: BackendApplication | undefined) => {
    setSession((previous) => ({ ...previous, backendApplication }));
  };

  return (
    <ApplicationContext.Provider
      value={{
        application: session.application,
        setApplication,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        backendApplication: session.backendApplication,
        setBackendApplication
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
};
